import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { messagingApi } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import {
  MessageCircle, X, Send, Plus, Hash, User,
  Package, Factory, Check, CheckCheck, Minus, Maximize2, ArrowLeft,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

// Notification sound
const playPing = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch {}
};

// Single chat window component
const ChatWindow = ({ channel, user, presence, onClose, onMinimize, isMinimized, navigate, onBack, isMobile }) => {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionResults, setMentionResults] = useState({ users: [], orders: [], factories: [] });
  const msgEndRef = useRef(null);
  const inputRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await messagingApi.getMessages(channel.id, 80);
      setMessages(res.data);
      await messagingApi.markRead(channel.id);
    } catch {}
  }, [channel.id]);

  useEffect(() => {
    fetchMessages();
    const iv = setInterval(fetchMessages, 3000);
    return () => clearInterval(iv);
  }, [fetchMessages]);

  useEffect(() => {
    if (!isMinimized) msgEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isMinimized]);

  useEffect(() => {
    if (!mentionOpen || !mentionQuery) { setMentionResults({ users: [], orders: [], factories: [] }); return; }
    const t = setTimeout(async () => {
      try { const res = await messagingApi.searchMentions(mentionQuery); setMentionResults(res.data); } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [mentionQuery, mentionOpen]);

  const handleSend = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      const mentionPattern = /@\[([^\]]+)\]\((\w+):([^)]+)\)/g;
      const mentions = { users: [], orders: [], factories: [] };
      let m;
      while ((m = mentionPattern.exec(draft)) !== null) {
        const [, , type, id] = m;
        if (type === 'user') mentions.users.push(id);
        else if (type === 'order') mentions.orders.push(id);
        else if (type === 'factory') mentions.factories.push(id);
      }
      await messagingApi.sendMessage(channel.id, { content: draft, mentions });
      setDraft('');
      fetchMessages();
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setDraft(val);
    const cursor = e.target.selectionStart;
    const textBefore = val.slice(0, cursor);
    const atMatch = textBefore.match(/@(\w*)$/);
    if (atMatch) { setMentionOpen(true); setMentionQuery(atMatch[1]); }
    else setMentionOpen(false);
  };

  const insertMention = (type, id, label) => {
    const cursor = inputRef.current?.selectionStart || draft.length;
    const textBefore = draft.slice(0, cursor);
    const textAfter = draft.slice(cursor);
    const atIdx = textBefore.lastIndexOf('@');
    setDraft(textBefore.slice(0, atIdx) + `@[${label}](${type}:${id}) ` + textAfter);
    setMentionOpen(false);
    inputRef.current?.focus();
  };

  const isOnline = (uid) => presence.find(p => p.id === uid)?.is_online || false;
  const getLastSeen = (uid) => {
    const u = presence.find(p => p.id === uid);
    if (!u?.last_seen) return 'Never';
    const diff = (Date.now() - new Date(u.last_seen).getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(u.last_seen).toLocaleDateString();
  };

  const getChannelName = () => {
    if (channel.type === 'channel') return `# ${channel.name}`;
    if (channel.member_names) {
      const other = Object.entries(channel.member_names).find(([k]) => k !== user?.id)?.[1];
      if (other) return other;
    }
    return 'Chat';
  };

  const handleMentionClick = (type, id, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (type === 'order') {
      navigate(`/orders/${id}`);
      // Close inbox + minimize all chat windows on navigate so the user sees the transition
      onClose?.();
    }
  };

  const renderContent = (content) => {
    const parts = content.split(/(@\[([^\]]+)\]\((\w+):([^)]+)\))/g);
    const elements = [];
    let i = 0;
    while (i < parts.length) {
      if (i + 4 < parts.length && parts[i + 1]?.startsWith('@[')) {
        const label = parts[i + 2], type = parts[i + 3], id = parts[i + 4];
        const clickable = type === 'order';
        elements.push(
          <span
            key={i}
            role={clickable ? 'link' : undefined}
            tabIndex={clickable ? 0 : undefined}
            className={cn(
              "inline-flex items-center gap-0.5 px-1 rounded font-semibold text-[11px] transition-colors",
              clickable && "cursor-pointer hover:underline hover:brightness-110 active:scale-[0.97]",
              type === 'user' ? "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400" :
              type === 'order' ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400" :
              "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400"
            )}
            onClick={clickable ? (e) => handleMentionClick(type, id, e) : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleMentionClick(type, id, e); } : undefined}
            data-testid={`mention-${type}-${id}`}
          >
            {type === 'user' && <User className="h-2.5 w-2.5" />}
            {type === 'order' && <Package className="h-2.5 w-2.5" />}
            {type === 'factory' && <Factory className="h-2.5 w-2.5" />}
            {label}
          </span>
        );
        i += 5;
      } else { if (parts[i]) elements.push(<span key={i}>{parts[i]}</span>); i++; }
    }
    return elements.length ? elements : content;
  };

  const fmtTime = (iso) => { if (!iso) return ''; return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); };
  const otherId = channel.type === 'dm' ? channel.members?.find(id => id !== user?.id) : null;
  const name = getChannelName();

  return (
    <div className={cn(
      "bg-white dark:bg-neutral-900 shadow-2xl shadow-black/15 border border-border flex flex-col overflow-hidden transition-all",
      isMobile
        ? "fixed inset-0 z-50 rounded-none border-0"
        : cn("rounded-t-xl border-b-0", isMinimized ? "w-[260px] h-auto" : "w-[328px] h-[420px]")
    )} data-testid={`chat-window-${channel.id}`}>
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white cursor-pointer flex-shrink-0" onClick={() => { if (isMinimized) onMinimize(false); }}>
        {isMobile && onBack && (
          <button onClick={(e) => { e.stopPropagation(); onBack(); }} className="hover:bg-white/20 rounded p-1 -ml-1" aria-label="Back" data-testid="chat-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        <div className="relative flex-shrink-0">
          {channel.type === 'channel' ? (
            <div className="h-7 w-7 rounded-full bg-white/20 flex items-center justify-center"><Hash className="h-3.5 w-3.5" /></div>
          ) : (
            <div className="h-7 w-7 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold">{name?.[0]?.toUpperCase()}</div>
          )}
          {otherId && isOnline(otherId) && <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 border-2 border-indigo-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold truncate">{name}</p>
          {!isMinimized && otherId && <p className="text-[9px] opacity-70">{isOnline(otherId) ? 'Online' : `Last seen ${getLastSeen(otherId)}`}</p>}
        </div>
        <div className="flex items-center gap-0.5">
          {!isMobile && (
            <button onClick={(e) => { e.stopPropagation(); onMinimize(!isMinimized); }} className="hover:bg-white/20 rounded p-0.5">
              {isMinimized ? <Maximize2 className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="hover:bg-white/20 rounded p-1" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
      </div>

      {/* Body — only when expanded */}
      {!isMinimized && (
        <>
          <ScrollArea className="flex-1 px-3 py-2">
            <div className="space-y-1">
              {messages.map((msg, i) => {
                const isMine = msg.sender_id === user?.id;
                const showSender = !isMine && (i === 0 || messages[i - 1]?.sender_id !== msg.sender_id);
                const readByOthers = (msg.read_by || []).filter(id => id !== msg.sender_id).length > 0;
                return (
                  <div key={msg.id} className={cn("flex flex-col", isMine ? "items-end" : "items-start")}>
                    {showSender && <span className="text-[9px] font-semibold text-muted-foreground ml-1 mb-0.5">{msg.sender_name}</span>}
                    <div className={cn("max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed", isMine ? "bg-indigo-600 text-white rounded-br-md" : "bg-muted/70 rounded-bl-md")}>
                      <span className="whitespace-pre-wrap break-words">{renderContent(msg.content)}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-0.5 px-1">
                      <span className="text-[9px] text-muted-foreground">{fmtTime(msg.created_at)}</span>
                      {isMine && (readByOthers ? <CheckCheck className="h-2.5 w-2.5 text-blue-500" /> : <Check className="h-2.5 w-2.5 text-muted-foreground" />)}
                    </div>
                  </div>
                );
              })}
              <div ref={msgEndRef} />
            </div>
          </ScrollArea>

          {/* Mention dropdown */}
          {mentionOpen && (mentionResults.users.length > 0 || mentionResults.orders.length > 0 || mentionResults.factories.length > 0) && (
            <div className="mx-3 mb-1 border rounded-lg bg-popover shadow-lg max-h-[120px] overflow-y-auto text-xs">
              {mentionResults.users.map(u => (
                <div key={u.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 cursor-pointer" onClick={() => insertMention('user', u.id, u.name)}>
                  <User className="h-3 w-3 text-blue-500" /><span className="font-medium">{u.name}</span><span className="text-muted-foreground text-[10px]">{u.role}</span>
                </div>
              ))}
              {mentionResults.orders.map(o => (
                <div key={o.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 cursor-pointer" onClick={() => insertMention('order', o.id, o.order_number)}>
                  <Package className="h-3 w-3 text-amber-500" /><span className="font-medium">{o.order_number}</span><span className="text-muted-foreground text-[10px]">{o.customer_name}</span>
                </div>
              ))}
              {mentionResults.factories.map(f => (
                <div key={f.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 cursor-pointer" onClick={() => insertMention('factory', f.id, f.name)}>
                  <Factory className="h-3 w-3 text-purple-500" /><span className="font-medium">{f.name}</span>
                </div>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="flex items-center gap-2 p-2 border-t flex-shrink-0">
            <Input ref={inputRef} value={draft} onChange={handleInputChange}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !mentionOpen) { e.preventDefault(); handleSend(); } }}
              placeholder="Type @ to mention..." className="text-sm flex-1 h-10 sm:h-9" data-testid="message-input" />
            <Button size="icon" className="h-10 w-10 sm:h-9 sm:w-9 bg-indigo-600 hover:bg-indigo-700 flex-shrink-0" onClick={handleSend} disabled={sending || !draft.trim()} data-testid="send-message-btn">
              <Send className="h-4 w-4 text-white" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

// Main floating chat controller
const FloatingChat = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [inboxOpen, setInboxOpen] = useState(false);
  const [channels, setChannels] = useState([]);
  const [presence, setPresence] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [openWindows, setOpenWindows] = useState([]); // [{channel, minimized}]
  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatType, setNewChatType] = useState('dm');
  const [newChannelName, setNewChannelName] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 640);
  const prevUnreadRef = useRef(0);

  // Track mobile breakpoint
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Heartbeat
  useEffect(() => {
    if (!user) return;
    messagingApi.heartbeat().catch(() => {});
    const hb = setInterval(() => messagingApi.heartbeat().catch(() => {}), 30000);
    return () => clearInterval(hb);
  }, [user]);

  // Fetch channels + presence
  const fetchChannels = useCallback(async () => {
    if (!user) return;
    try {
      const [chRes, prRes] = await Promise.all([messagingApi.getChannels(), messagingApi.getPresence()]);
      setChannels(chRes.data);
      setPresence(prRes.data);
      const newTotal = chRes.data.reduce((sum, c) => sum + (c.unread || 0), 0);
      // Ping if new unread
      if (newTotal > prevUnreadRef.current) playPing();
      prevUnreadRef.current = newTotal;
      setTotalUnread(newTotal);
    } catch {}
  }, [user]);

  useEffect(() => { fetchChannels(); const iv = setInterval(fetchChannels, 5000); return () => clearInterval(iv); }, [fetchChannels]);

  const openChat = (ch) => {
    if (openWindows.some(w => w.channel.id === ch.id)) {
      setOpenWindows(prev => prev.map(w => w.channel.id === ch.id ? { ...w, minimized: false } : w));
    } else if (isMobile) {
      // On mobile, only one chat window at a time (full-screen takeover)
      setOpenWindows([{ channel: ch, minimized: false }]);
    } else {
      setOpenWindows(prev => [...prev.slice(-3), { channel: ch, minimized: false }]); // max 4 windows
    }
    setInboxOpen(false);
  };

  // Close all chat windows and inbox — used when navigating via @mention
  const closeAll = () => {
    setOpenWindows([]);
    setInboxOpen(false);
  };

  const closeWindow = (channelId) => setOpenWindows(prev => prev.filter(w => w.channel.id !== channelId));
  const toggleMinimize = (channelId, min) => setOpenWindows(prev => prev.map(w => w.channel.id === channelId ? { ...w, minimized: min } : w));

  const getChannelName = (ch) => {
    if (ch.type === 'channel') return `# ${ch.name}`;
    if (ch.member_names) { const n = Object.entries(ch.member_names).find(([k]) => k !== user?.id)?.[1]; if (n) return n; }
    return 'Chat';
  };

  const isOnline = (uid) => presence.find(p => p.id === uid)?.is_online || false;

  const handleCreateChannel = async () => {
    if (newChatType === 'dm' && selectedMembers.length === 1) {
      try {
        const res = await messagingApi.createChannel({ type: 'dm', member_ids: [selectedMembers[0].id] });
        openChat(res.data);
        setShowNewChat(false); setSelectedMembers([]);
        fetchChannels();
      } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    } else if (newChatType === 'channel' && newChannelName.trim()) {
      try {
        const res = await messagingApi.createChannel({ type: 'channel', name: newChannelName.trim(), member_ids: selectedMembers.map(m => m.id) });
        openChat(res.data);
        setShowNewChat(false); setNewChannelName(''); setSelectedMembers([]);
        fetchChannels();
      } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    }
  };

  if (!user || user.role === 'factory') return null;

  return (
    <div
      className="fixed bottom-0 right-0 z-50 flex items-end gap-1.5 pr-2 sm:pr-4"
      data-testid="chat-footer"
    >
      {/* Open chat windows */}
      {openWindows.map(w => (
        <ChatWindow
          key={w.channel.id}
          channel={w.channel}
          user={user}
          presence={presence}
          isMinimized={w.minimized}
          onClose={() => {
            if (isMobile) closeAll();
            else closeWindow(w.channel.id);
          }}
          onMinimize={(min) => toggleMinimize(w.channel.id, min)}
          onBack={isMobile ? () => { closeWindow(w.channel.id); setInboxOpen(true); } : undefined}
          isMobile={isMobile}
          navigate={navigate}
        />
      ))}

      {/* Inbox / Main messaging bar */}
      <div className="flex flex-col items-end">
        {/* Inbox panel */}
        {inboxOpen && (
          <div
            className={cn(
              "bg-white dark:bg-neutral-900 shadow-2xl shadow-black/15 border border-border flex flex-col overflow-hidden",
              isMobile
                ? "fixed inset-0 z-50 rounded-none border-0"
                : "w-[320px] h-[420px] rounded-t-xl border-b-0 mb-0"
            )}
            data-testid="chat-inbox"
          >
            <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 text-white flex-shrink-0">
              <div className="flex-1">
                <p className="text-sm font-semibold">Messaging</p>
                <p className="text-[9px] opacity-70">{presence.filter(p => p.is_online && p.id !== user?.id).length} online</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => { setShowNewChat(true); }} className="hover:bg-white/20 rounded p-1.5" title="New chat" aria-label="New chat"><Plus className="h-4 w-4" /></button>
                <button onClick={() => setInboxOpen(false)} className="hover:bg-white/20 rounded p-1.5" aria-label="Close">
                  {isMobile ? <X className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {showNewChat ? (
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                <div className="flex gap-2">
                  <Button size="sm" variant={newChatType === 'dm' ? 'default' : 'outline'} onClick={() => setNewChatType('dm')} className="text-xs flex-1">
                    <User className="h-3 w-3 mr-1" /> DM
                  </Button>
                  <Button size="sm" variant={newChatType === 'channel' ? 'default' : 'outline'} onClick={() => setNewChatType('channel')} className="text-xs flex-1">
                    <Hash className="h-3 w-3 mr-1" /> Channel
                  </Button>
                </div>
                {newChatType === 'channel' && (
                  <Input placeholder="Channel name..." value={newChannelName} onChange={e => setNewChannelName(e.target.value)} className="text-xs" data-testid="channel-name-input" />
                )}
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{newChatType === 'dm' ? 'Select person' : 'Add members'}</p>
                <div className="space-y-1 max-h-[240px] overflow-y-auto">
                  {allUsers.filter(u => u.id !== user?.id).map(u => {
                    const sel = selectedMembers.some(m => m.id === u.id);
                    return (
                      <div key={u.id} className={cn("flex items-center gap-2 p-2 rounded-lg cursor-pointer text-xs", sel ? "bg-indigo-50 dark:bg-indigo-500/10 ring-1 ring-indigo-300" : "hover:bg-muted/50")}
                        onClick={() => { if (newChatType === 'dm') setSelectedMembers([u]); else setSelectedMembers(p => sel ? p.filter(m => m.id !== u.id) : [...p, u]); }}>
                        <div className="relative">
                          <div className="h-7 w-7 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-[10px] font-bold text-indigo-700 dark:text-indigo-400">{u.name?.[0]?.toUpperCase()}</div>
                          {isOnline(u.id) && <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 border-2 border-white dark:border-neutral-900" />}
                        </div>
                        <div className="flex-1 min-w-0"><p className="font-medium truncate">{u.name}</p><p className="text-[9px] text-muted-foreground">{u.role}</p></div>
                        {sel && <Check className="h-4 w-4 text-indigo-600" />}
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => setShowNewChat(false)}>Cancel</Button>
                  <Button size="sm" className="flex-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleCreateChannel}
                    disabled={newChatType === 'dm' ? selectedMembers.length !== 1 : !newChannelName.trim()} data-testid="create-channel-btn">Create</Button>
                </div>
              </div>
            ) : (
              <ScrollArea className="flex-1 px-2 py-1">
                {channels.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground text-xs">
                    <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p>No conversations yet</p>
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {channels.map(ch => {
                      const dmOther = ch.type === 'dm' ? ch.members?.find(id => id !== user?.id) : null;
                      return (
                        <div key={ch.id} className={cn("flex items-center gap-2.5 p-2 rounded-lg cursor-pointer transition-colors hover:bg-muted/50", ch.unread > 0 && "bg-indigo-50 dark:bg-indigo-500/5")}
                          onClick={() => openChat(ch)} data-testid={`channel-${ch.id}`}>
                          <div className="relative flex-shrink-0">
                            {ch.type === 'channel' ? (
                              <div className="h-8 w-8 rounded-full bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center"><Hash className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400" /></div>
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-xs font-bold text-indigo-700 dark:text-indigo-400">{getChannelName(ch)?.[0]?.toUpperCase()}</div>
                            )}
                            {dmOther && isOnline(dmOther) && <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 border-2 border-white dark:border-neutral-900" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className={cn("text-xs font-semibold truncate", ch.unread > 0 && "text-indigo-700 dark:text-indigo-400")}>{getChannelName(ch)}</p>
                              {ch.last_message && <span className="text-[9px] text-muted-foreground flex-shrink-0 ml-1">{new Date(ch.last_message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
                            </div>
                            {ch.last_message && <p className="text-[10px] text-muted-foreground truncate">{ch.last_message.sender_name}: {ch.last_message.content}</p>}
                          </div>
                          {ch.unread > 0 && <span className="h-5 min-w-[20px] px-1 rounded-full bg-indigo-600 text-[10px] font-bold text-white flex items-center justify-center">{ch.unread}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            )}
          </div>
        )}

        {/* Messaging bar (always visible at bottom) */}
        <div
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-t-xl shadow-lg cursor-pointer hover:opacity-95 transition-opacity w-[220px] sm:w-[260px]"
          onClick={() => { setInboxOpen(p => !p); setShowNewChat(false); if (!inboxOpen) { messagingApi.getPresence().then(r => setAllUsers(r.data)).catch(() => {}); } }}
          data-testid="chat-bar"
        >
          <MessageCircle className="h-4 w-4 flex-shrink-0" />
          <p className="text-xs font-semibold flex-1">Messaging</p>
          {totalUnread > 0 && (
            <span className="h-5 min-w-[20px] px-1.5 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center animate-pulse">{totalUnread}</span>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setInboxOpen(true); setShowNewChat(true);
              messagingApi.getPresence().then(r => setAllUsers(r.data)).catch(() => {});
            }}
            className="hover:bg-white/20 rounded p-0.5 -mr-1"
            aria-label="New chat"
            data-testid="new-chat-btn"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FloatingChat;
